import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, Loader2, Upload } from 'lucide-react';
import { Holding, subscribeToHoldings, removeHolding } from '../../services/holdingsService';
import { fetchWithDelay } from '../../lib/utils';
import { HoldingRow } from './HoldingRow';
import { AddPositionModal } from './AddPositionModal';
import { PerformanceChart } from './PerformanceChart';
import { ImportTransactionsModal } from './ImportTransactionsModal';

interface PortfolioProps {
  uid: string;
  onAnalyzeTicker: (ticker: string) => void;
}

export function PortfolioDesktop({ uid, onAnalyzeTicker }: PortfolioProps) {
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [prices, setPrices] = useState<Record<string, number>>({});

  useEffect(() => {
    const unsubscribe = subscribeToHoldings(uid, (newHoldings) => {
      setHoldings(newHoldings);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [uid]);

  useEffect(() => {
    if (holdings.length === 0) return;
    
    fetchWithDelay(
      holdings,
      async (h) => {
        try {
          const res = await fetch(`/api/fmp/stable/quote?symbol=${h.ticker}`);
          if (res.ok) {
            const data = await res.json();
            if (data?.[0]?.price) return { ticker: h.ticker, price: data[0].price };
          }
        } catch {}
        return { ticker: h.ticker, price: null };
      },
      250
    ).then(results => {
      const map: Record<string, number | null> = {};
      results.forEach(r => { if (r) map[r.ticker] = r.price; });
      setPrices(map as Record<string, number>);
    });
  }, [holdings]);

  const handleRemove = async (ticker: string, docId?: string) => {
    try {
      await removeHolding(uid, ticker, docId);
    } catch (err) {
      console.error("Failed to remove holding", err);
    }
  };

  const handleKeeperComplete = (ticker: string, verdict: 'KEEP' | 'WATCH' | 'EXIT') => {
    // Optimistic update in case Firestore sync is slightly delayed
    setHoldings(prev => prev.map(h => 
      h.ticker === ticker ? { ...h, lastKeeperVerdict: verdict } : h
    ));
  };

  const keepCount = holdings.filter(h => h.lastKeeperVerdict === 'KEEP').length;
  const watchCount = holdings.filter(h => h.lastKeeperVerdict === 'WATCH').length;
  const exitCount = holdings.filter(h => h.lastKeeperVerdict === 'EXIT').length;
  const notAssessedCount = holdings.filter(h => !h.lastKeeperVerdict).length;
  const existingTickers = new Set(holdings.map(h => h.ticker));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-ink-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-serif font-medium italic tracking-tight mb-2">Oracle Insight Portfolio</h2>
          <p className="text-ink-500 font-medium tracking-tight">Your holdings assessed by The Keeper.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-surface-2 text-ink-600 rounded-lg hover:bg-surface transition-colors shrink-0 font-medium uppercase tracking-wider text-xs"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-oxblood text-paper rounded-lg hover:bg-oxblood-800 transition-colors shrink-0 font-medium uppercase tracking-wider text-xs"
          >
            <Plus className="w-4 h-4" />
            Add Position
          </button>
        </div>
      </div>

      {holdings.length === 0 ? (
        <div className="oi-card p-12 flex flex-col items-center justify-center text-center mt-12">
          <div className="w-16 h-16 bg-surface-2 rounded-lg flex items-center justify-center mb-4">
            <Briefcase className="w-8 h-8 text-ink-300" />
          </div>
          <h3 className="text-lg font-medium text-ink mb-2">No positions tracked yet</h3>
          <p className="text-sm text-ink-500 mb-6 max-w-sm">
            Add your first holding to get started with The Keeper and evaluate your portfolio using the Oracle Insight signals.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-oxblood text-paper rounded-lg hover:bg-oxblood-800 transition-colors font-medium uppercase tracking-wider text-xs"
          >
            <Plus className="w-4 h-4" />
            Add Position
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {holdings.length >= 3 && (
            <div className="flex flex-wrap items-center gap-3 text-xs font-medium uppercase tracking-wider text-ink-500 border-b border-ink-200 pb-4 px-2">
              <span className="text-ink">{holdings.length} positions</span>
              <span className="text-ink-300 px-1 hidden sm:inline-block">·</span>
              <span className="text-success-700">{keepCount} KEEP</span>
              <span className="text-ink-300 px-1 hidden sm:inline-block">·</span>
              <span className="text-warning-700">{watchCount} WATCH</span>
              <span className="text-ink-300 px-1 hidden sm:inline-block">·</span>
              <span className="text-danger-700">{exitCount} EXIT</span>
              <span className="text-ink-300 px-1 hidden sm:inline-block">·</span>
              <span>{notAssessedCount} unassessed</span>
            </div>
          )}

          <div className="oi-card p-6">
            <h3 className="text-sm font-medium uppercase tracking-wider text-ink-500 mb-4">
              Performance vs S&P 500
            </h3>
            <PerformanceChart holdings={holdings} prices={prices} />
          </div>

          <div className="oi-card">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[700px] md:min-w-full">
              <thead>
                <tr className="border-b border-ink-200 bg-surface-2">
                  <th className="px-4 py-3 text-xs font-medium text-ink-500 uppercase tracking-wider w-[25%] lg:w-[35%]">Asset</th>
                  <th className="px-4 py-3 text-xs font-medium text-ink-500 uppercase tracking-wider text-right hidden sm:table-cell">Shares</th>
                  <th className="px-4 py-3 text-xs font-medium text-ink-500 uppercase tracking-wider text-right hidden sm:table-cell">Cost Basis</th>
                  <th className="px-4 py-3 text-xs font-medium text-ink-500 uppercase tracking-wider text-right hidden md:table-cell">Current</th>
                  <th className="px-4 py-3 text-xs font-medium text-ink-500 uppercase tracking-wider text-center w-[15%]">Keeper</th>
                  <th className="px-4 py-3 text-xs font-medium text-ink-500 uppercase tracking-wider text-right w-[20%]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding) => (
                  <HoldingRow 
                    key={holding.ticker} 
                    holding={holding} 
                    uid={uid} 
                    onRemove={handleRemove} 
                    onAnalyze={onAnalyzeTicker}
                    onKeeperComplete={handleKeeperComplete}
                    currentPrice={prices[holding.ticker] ?? null}
                  />
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddPositionModal 
          uid={uid} 
          onClose={() => setShowAddModal(false)}
          onAdded={() => { setShowAddModal(false) }}
        />
      )}

      {showImportModal && (
        <ImportTransactionsModal
          uid={uid}
          existingTickers={existingTickers}
          onClose={() => setShowImportModal(false)}
          onImported={() => setShowImportModal(false)}
        />
      )}
    </div>
  );
}
